import { createApp } from 'vue'
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'

import App from './App.vue'
import SplashScreen from './components/SplashScreen.vue'
import { router } from './router'
import { i18n } from './i18n'
import { ApiError, setAuthErrorHandlers } from './api'
import { denialOf, denyAccess, initSession, markExpired } from './auth'

// Element Plus styles are imported from main.css, inside a cascade layer,
// so Tailwind utilities always win over a component default.
// Poppins, the face of the design mockups; bundled, so no font CDN is needed.
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/poppins/700.css'
import './styles/main.css'

const app = createApp(App)

const queryOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        // A 401 or 403 is an answer, not a glitch: retrying cannot change
        // it and only delays the redirect the user is waiting for.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) {
            return false
          }
          return failureCount < 2
        },
        staleTime: 60_000,
        // The backend refreshes the rent roll every few minutes and the
        // interface shows the time of the last load. Refetching on every
        // window focus would add requests without adding freshness.
        refetchOnWindowFocus: false,
      },
    },
  },
}

// What the app does when the backend rejects a request, defined once so no
// screen has to decide for itself what an expired session looks like.
setAuthErrorHandlers({
  // The client already renewed the token silently and retried once; a 401
  // that reaches this point means the session is over.
  onUnauthorized: () => {
    markExpired()
    const current = router.currentRoute.value
    // Before the first navigation (startup) the guard decides.
    if (current.name && current.name !== 'login') {
      void router.push({ name: 'login', query: { redirect: current.fullPath } })
    }
  },
  onForbidden: (error) => {
    // 403 user_not_registered / user_inactive: authenticated with Entra but
    // not (or no longer) authorised here. Any other 403 (forbidden,
    // out_of_scope) is the answer to one request, shown by its screen.
    const reason = denialOf(error)
    if (!reason) return
    denyAccess(reason)
    if (router.currentRoute.value.name && router.currentRoute.value.name !== 'access-request') {
      void router.push({ name: 'access-request' })
    }
  },
})

async function bootstrap() {
  // Branded splash while the session resolves, instead of a blank page. It is
  // a separate minimal app so it can paint before the router exists.
  const splash = createApp(SplashScreen).use(i18n)
  splash.mount('#app')

  // Completes a pending Entra redirect and reads /me BEFORE the router
  // starts, so its first guard decision already knows who the user is.
  try {
    await initSession()
  } finally {
    splash.unmount()
  }

  app.use(i18n)
  app.use(router)
  app.use(VueQueryPlugin, queryOptions)

  app.mount('#app')
}

void bootstrap()
